import React, { useEffect, useState } from 'react';
import { Button, Card, Table, Space, message, Input, Tag, Tooltip, Tabs } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'underscore';
import dayjs from 'dayjs';

import { AppDispatch, CombinedStates } from '../../../core/types';
import { getCompetitions, createCompetition } from '../../../core/competition/competitionApi';
import { loadSingleCompetition } from '../../../core/competition/competitionSlice';
import { Competition, CompetitionType, CreateCompetitionRequest } from '../../../core/competition/types';
import CreateCompetitionModal from '../CreateCompetitionModal';
import CompetitionFilterModal, { CompetitionFilters } from '../CompetitionFilterModal';
import CompetitionStatistics from '../CompetitionStatistics';
import { subjectOptions, getSubjectInfo, competitionTypeOptions, getCompetitionTypeInfo, getGradeInfo } from '../../../core/utils/helpers';
import './competitionsListing.scss';

const CompetitionsListing = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filters, setFilters] = useState<CompetitionFilters>({
        subjects: [],
        types: [],
        imported: null,
    });

    const {
        competitions,
        isFetching,
        error,
    } = useSelector((state: CombinedStates) => state.competition);

    useEffect(() => {
        if (isEmpty(competitions)) {
            dispatch(getCompetitions());
        }
    }, [dispatch]);

    const handleCreateCompetition = async (values: CreateCompetitionRequest) => {
        try {
            const result = await dispatch(createCompetition(values)).unwrap();
            message.success('Konkursas sėkmingai sukurtas!');
            setIsCreateModalVisible(false);
            // Load the competition and redirect to the competition detail page
            if (result.competition?.id) {
                dispatch(loadSingleCompetition(result.competition.id));
                navigate('/konkursas');
            }
        } catch (err) {
            message.error('Klaida kuriant konkursą');
        }
    };

    const handleEditCompetition = (competitionId: number) => {
        dispatch(loadSingleCompetition(competitionId));
        navigate('/konkursas');
    };

    const handleApplyFilters = (newFilters: CompetitionFilters) => {
        setFilters(newFilters);
        setIsFilterModalVisible(false);
    };

    const handleReload = () => {
        dispatch(getCompetitions());
        setSearchText('');
        setFilters({
            subjects: [],
            types: [],
            imported: null,
        });
    };

    const applyFilters = (competitionsList: Competition[]) => competitionsList.filter((competition: Competition) => {
        // Text search filter
        const matchesSearch = competition.title.toLowerCase().includes(searchText.toLowerCase())
            || competition.subject.toLowerCase().includes(searchText.toLowerCase())
            || competition.organizer.toLowerCase().includes(searchText.toLowerCase());

        if (!matchesSearch) return false;

        // Subject filter
        if (filters.subjects.length > 0 && !filters.subjects.includes(competition.subject)) {
            return false;
        }

        // Type filter
        if (filters.types.length > 0 && !filters.types.includes(competition.type)) {
            return false;
        }

        // Imported filter
        if (filters.imported !== null) {
            const importedValue = filters.imported === 'true';
            if (competition.imported !== importedValue) {
                return false;
            }
        }

        return true;
    });

    const filteredCompetitions = applyFilters(competitions);

    const columns = [
        {
            title: 'Pavadinimas',
            dataIndex: 'title',
            key: 'title',
            render: (text: string, record: Competition) => (
                <Button
                    type="link"
                    onClick={() => {
                        if (record.id) {
                            dispatch(loadSingleCompetition(record.id));
                            navigate('/konkursas');
                        }
                    }}
                    style={{
                        padding: 0,
                        height: 'auto',
                        maxWidth: 320,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        textAlign: 'left',
                        display: 'inline-block',
                    }}>
                    {text}
                </Button>
            ),
        },
        {
            title: 'Dalykas',
            dataIndex: 'subject',
            key: 'subject',
            render: (subject: string) => {
                const subjectOption = getSubjectInfo(subject);
                return (
                    <Tag style={{
                        backgroundColor: subjectOption?.color || '#8c8c8c',
                        color: 'white',
                        border: 'none',
                    }}>
                        {subjectOption?.label || subject}
                    </Tag>
                );
            },
        },
        {
            title: 'Tipas',
            dataIndex: 'type',
            key: 'type',
            render: (type: CompetitionType) => {
                const typeOption = getCompetitionTypeInfo(type);
                return (
                    <Tag style={{
                        backgroundColor: typeOption?.color || '#8c8c8c',
                        color: 'white',
                        border: 'none',
                    }}>
                        {typeOption?.label || type}
                    </Tag>
                );
            },
        },
        {
            title: 'Data',
            dataIndex: 'date',
            key: 'date',
            render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: 'Renginio klasės',
            key: 'grades',
            render: (record: Competition) => {
                const fromGrade = getGradeInfo(record.grade_from);
                const toGrade = getGradeInfo(record.grade_to);

                if (record.grade_from === record.grade_to) {
                    return fromGrade?.label || record.grade_from;
                }

                return `${fromGrade?.label || record.grade_from} - ${toGrade?.label || record.grade_to}`;
            },
        },
        {
            title: 'Dalyviai',
            dataIndex: 'participants_total',
            key: 'participants_total',
        },
        {
            title: 'Nugalėtojai',
            key: 'winners_count',
            render: (record: Competition) => {
                const winnersCount = record.winners?.length || 0;
                return winnersCount > 0 ? winnersCount : '-';
            },
        },
        // {
        //     title: 'Organizatorius',
        //     dataIndex: 'organizer',
        //     key: 'organizer',
        // },
        {
            title: 'Importuotas',
            dataIndex: 'imported',
            key: 'imported',
            render: (imported: boolean) => (
                <Tag>
                    {imported ? 'Taip' : 'Ne'}
                </Tag>
            ),
        },
        {
            title: 'Veiksmai',
            key: 'actions',
            render: (record: Competition) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => record.id && handleEditCompetition(record.id)}>
                        Redaguoti
                    </Button>
                </Space>
            ),
        },
    ];

    const administrationTab = (
        <Card>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                    <Input
                        placeholder="Ieškoti konkursų..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 300 }} />
                    <Tooltip title="Filtruoti konkursus">
                        <Button
                            icon={<FilterOutlined />}
                            onClick={() => setIsFilterModalVisible(true)}>
                            Filtruoti
                        </Button>
                    </Tooltip>
                    <Tooltip title="Atnaujinti informaciją">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleReload}
                            loading={isFetching} />
                    </Tooltip>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsCreateModalVisible(true)}>
                        Sukurti konkursą
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={filteredCompetitions}
                rowKey="id"
                loading={isFetching}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} iš ${total} konkursų`,
                }} />
        </Card>
    );

    const items = [
        {
            key: 'administration',
            label: 'Administravimas',
            children: administrationTab,
        },
        {
            key: 'statistics',
            label: 'Statistika',
            children: <CompetitionStatistics />,
        },
    ];

    return (
        <div className="competitions-listing-container">
            <Tabs items={items} type="card" className="competitions-tabs" />

            <CreateCompetitionModal
                visible={isCreateModalVisible}
                onCancel={() => setIsCreateModalVisible(false)}
                onSubmit={handleCreateCompetition}
                loading={isFetching} />

            <CompetitionFilterModal
                visible={isFilterModalVisible}
                onCancel={() => setIsFilterModalVisible(false)}
                onApply={handleApplyFilters}
                currentFilters={filters} />
        </div>
    );
};

export default CompetitionsListing;
